<script setup>
    import { ref, computed, watchEffect, watch } from 'vue';
    import { useMainStore } from '@/stores/store.js';
    import { Validations } from '@/validations.js';
    import { PhAsterisk, PhX } from '@phosphor-icons/vue';
    import { useRouter, useRoute } from 'vue-router';
    import { fetchRoomTypeData } from '@/data.js';

    const store = useMainStore();
    const router = useRouter();
    const route = useRoute();

    const props = defineProps({
        realtime: {
            type: Boolean,
            default: true,
            required: true,
        },
    });

    //
    // Form data local to this step. So we can work with if here and then update to store when needed.
    //
    const formData = ref({
        unit: store.user.room.unit,
        width: store.user.room.width,
        height: store.user.room.height,
        length: store.user.room.length,
        materials: Array.isArray(store.user.room.materials) ? [...store.user.room.materials] : [],
        usage: store.user.room.usage,
        usagetarget: store.user.room.usagetarget,
    });

    watch(
        () => [store.syncRoomData],
        (newValue, oldValue) => {
            syncFormFromStore();
            store.syncRoomData = false; // turn off the flag
        },
    );

    // Load room data
    const roomTypes = ref(null);
    const loadRoomData = async () => {
        try {
            const data = await fetchRoomTypeData(); // Fetch room data based on ID
            // $log.log(`data : ${JSON.stringify(data, null, 4)}`);
            if (data) {
                roomTypes.value = data;
            } else {
                console.error('Room data not found');
            }
        } catch (error) {
            console.error('Error fetching room data:', error);
        }
    };
    // Watch the route and fetch data when the component is mounted or route changes
    watchEffect(() => {
        if (roomTypes.value === null) {
            loadRoomData();
        }
    });

    /*
    ------------------------------------------------------------------------
    Surface NRC data — structure (identifiers, surfaces, labels) is defined
    in store-state.js and is the source of truth. The spreadsheet import
    only updates NRC values; it cannot change identifiers, labels, or
    surface groupings.
    ------------------------------------------------------------------------
    */
    const surfaceNrcData = computed(() => store.surfaceNrc);

    const materialTypes = computed(() =>
        surfaceNrcData.value
            .filter((entry) => entry.label && entry.label.trim() !== '')
            .map((entry) => ({
                identifier: entry.identifier,
                label: entry.label,
                nrc: parseFloat(entry.nrc),
            }))
    );

    /*
    ------------------------------------------------------------------------
    Map surface group names (from store-state.js) to store user property
    keys. These are code-controlled — not dependent on spreadsheet data.
    ------------------------------------------------------------------------
    */
    const surfaceToStoreKey = {
        'Walls': 'walls_nrc',
        'Ceiling': 'ceiling_nrc',
        'Floor': 'floor_nrc',
    };

    /*
    ------------------------------------------------------------------------
    Resolve NRC values for all surfaces based on selected materials.

    For each surface group (Walls, Ceiling, Floor):
    - If the user has selected a material checkbox for that surface, use
      its NRC value.
    - Otherwise, use the default entry (the one with a blank label).
    ------------------------------------------------------------------------
    */
    const extractMaterialAbsorptions = () => {
        const data = surfaceNrcData.value;
        const selectedMaterials = store.user.room.materials;

        for (const [surface, storeKey] of Object.entries(surfaceToStoreKey)) {
            const surfaceEntries = data.filter((e) => e.surface === surface);

            // Check if the user selected any material for this surface
            const selected = surfaceEntries.find(
                (e) => e.label && e.label.trim() !== '' && selectedMaterials.includes(e.identifier)
            );

            // Use selected material NRC, or fall back to the default entry
            const defaultEntry = surfaceEntries.find((e) => !e.label || e.label.trim() === '');

            store.user[storeKey] = selected
                ? parseFloat(selected.nrc)
                : defaultEntry
                    ? parseFloat(defaultEntry.nrc)
                    : 0;
        }
    };

    // Set initial NRC values on component load
    extractMaterialAbsorptions();

    //
    // Update form data with store values
    //
    const syncFormFromStore = () => {
        formData.value.unit = store.user.room.unit;
        formData.value.width = store.user.room.width;
        formData.value.height = store.user.room.height;
        formData.value.length = store.user.room.length;
        formData.value.materials = Array.isArray(store.user.room.materials) ? [...store.user.room.materials] : [];
        formData.value.usage = store.user.room.usage;
        const selectedRoomType = roomTypes.value.find((type) => type.value === formData.value.usage);
        if (selectedRoomType) {
            store.user.room.usagetarget = selectedRoomType.target;
            $log.log(`[RoomDataForm:syncFormFromStore] store.user.room.usagetarget: ${JSON.stringify(store.user.room.usagetarget, null, 4)}`);
        }
    };

    //
    // Update store with room data in the local form (fields)
    //
    const syncStoreRoomData = (force) => {
        if (props.realtime || force) {
            store.user.room.unit = formData.value.unit;
            store.user.room.width = formData.value.width;
            store.user.room.height = formData.value.height;
            store.user.room.length = formData.value.length;
            store.user.room.materials = formData.value.materials;
            store.user.room.usage = formData.value.usage;
            extractMaterialAbsorptions();
            const selectedRoomType = roomTypes.value.find((type) => type.value === formData.value.usage);
            if (selectedRoomType) {
                store.user.room.usagetarget = selectedRoomType.target;
            }
        }
    };

    const handleRoomDoneClick = () => {
        // Update the store (not realtime).
        syncStoreRoomData(true);

        if (Validations.validateRoom(store)) {
            store.validation.Room.showErrors = false;
            store.showRoomForm = false;
        } else {
            store.validation.Room.showErrors = true;
            store.showRoomForm = true;
        }
    };

    const handleRoomCancelClick = () => {
        syncFormFromStore();
        store.validation.Room.showErrors = false;
        store.showRoomForm = false;
    };

    const getMaxValue = () => {
        if (store.user.room.unit === 'metric') {
            return 50;
        } else {
            return 150; // imperial
        }
    };
</script>

<template>
    <div class="relative mt-5 grid grid-cols-1 gap-x-6 gap-y-8 first-of-type:mt-0 sm:grid-cols-6">
        <!-- Unit -->
        <div class="sm:col-span-2">
            <label for="unit" class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                Unit
                <span class="sr-only">(required)</span>
                <PhAsterisk weight="bold" class="lux-required-mark" aria-hidden="true" />
            </label>
            <div class="mt-2">
                <select
                    id="unit"
                    name="unit"
                    v-model="formData.unit"
                    @change="syncStoreRoomData()"
                    class="peer block w-full border-0 border-b border-black bg-gray-50 px-1.5 text-sm text-gray-900 shadow-sm ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:leading-6 md:text-base">
                    <option value="imperial" selected>Feet (Imperial)</option>
                    <option value="metric">Meters (Metric)</option>
                </select>
            </div>
        </div>

        <div class="hidden sm:col-span-4 sm:block"></div>

        <!-- Room Length -->
        <div class="sm:col-span-2">
            <label for="roomlength" class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                Room length
                <span class="text-xs font-normal lowercase text-blacka50" v-if="formData.unit === 'imperial'"> (Feet) </span>
                <span class="text-xs font-normal lowercase text-blacka50" v-if="formData.unit === 'metric'"> (Meters) </span>
                <span class="sr-only">(required)</span>
                <PhAsterisk weight="bold" class="lux-required-mark" aria-hidden="true" />
            </label>
            <div class="mt-2">
                <input
                    type="number"
                    name="roomlength"
                    id="roomlength"
                    min="1"
                    :max="getMaxValue()"
                    v-model="formData.length"
                    @change="syncStoreRoomData()"
                    placeholder="Length"
                    aria-labelledby="roomlength"
                    step="0.1"
                    class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-sm text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:leading-6 md:text-base" />
            </div>
            <p class="mt-1 text-sm text-red-600" id="roomlength-error" v-if="!store.validateRoomLength && store.validation.Room.showErrors">
                {{ store.validation.Room.errors.roomlength }}
            </p>
        </div>

        <!-- Room Width -->
        <div class="sm:col-span-2">
            <label for="roomwidth" class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                Room width
                <span class="text-xs font-normal lowercase text-blacka50" v-if="formData.unit === 'imperial'"> (Feet) </span>
                <span class="text-xs font-normal lowercase text-blacka50" v-if="formData.unit === 'metric'"> (Meters) </span>
                <span class="sr-only">(required)</span>
                <PhAsterisk weight="bold" class="lux-required-mark" aria-hidden="true" />
            </label>
            <div class="mt-2">
                <input
                    type="number"
                    name="roomwidth"
                    id="roomwidth"
                    min="1"
                    :max="getMaxValue()"
                    v-model="formData.width"
                    @change="syncStoreRoomData()"
                    placeholder="Width"
                    aria-labelledby="roomwidth"
                    step="0.1"
                    class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-sm text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:leading-6 md:text-base" />
            </div>
            <p class="mt-1 text-sm text-red-600" id="roomwidth-error" v-if="!store.validateRoomWidth && store.validation.Room.showErrors">
                {{ store.validation.Room.errors.roomwidth }}
            </p>
        </div>

        <!-- Room Height -->
        <div class="sm:col-span-2">
            <label for="roomheight" class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                Room height
                <span class="text-xs font-normal lowercase text-blacka50" v-if="formData.unit === 'imperial'"> (Feet) </span>
                <span class="text-xs font-normal lowercase text-blacka50" v-if="formData.unit === 'metric'"> (Meters) </span>
                <span class="sr-only">(required)</span>
                <PhAsterisk weight="bold" class="lux-required-mark" aria-hidden="true" />
            </label>
            <div class="mt-2">
                <input
                    type="number"
                    name="roomheight"
                    id="roomheight"
                    min="1"
                    :max="getMaxValue()"
                    v-model="formData.height"
                    @change="syncStoreRoomData()"
                    placeholder="Height"
                    aria-labelledby="roomheight"
                    step="0.1"
                    class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-sm text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-xs sm:leading-6 md:text-base" />
            </div>
            <p class="mt-1 text-sm text-red-600" id="roomheight-error" v-if="!store.validateRoomHeight && store.validation.Room.showErrors">
                {{ store.validation.Room.errors.roomheight }}
            </p>
        </div>

        <!-- Intended Use -->
        <div class="sm:col-span-6">
            <label for="roomusage" class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                Intended use
                <span class="sr-only">(required)</span>
                <PhAsterisk weight="bold" class="lux-required-mark" aria-hidden="true" />
            </label>
            <p class="mt-2 text-xs text-gray-500">Please select the intended usage of teh room from the list provided which will allow for calculation in line with global reverberation time standards.</p>
            <div class="mt-2">
                <select
                    id="roomusage"
                    name="roomusage"
                    v-model="formData.usage"
                    @change="syncStoreRoomData()"
                    class="block w-full border-0 border-b border-black bg-gray-50 px-1.5 text-sm text-gray-900 shadow-sm ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:max-w-md sm:leading-6 md:text-base">
                    <option value="" selected>Select...</option>
                    <option v-for="(room, index) in roomTypes" :key="index" :value="room.value">
                        {{ room.label }}
                    </option>
                </select>
            </div>
            <p class="mt-1 text-sm text-red-600" id="roomusage-error" v-if="!store.validateRoomUsage && store.validation.Room.showErrors">
                {{ store.validation.Room.errors.roomusage }}
            </p>
        </div>

        <div class="hidden sm:col-span-4 sm:block"></div>

        <!-- Acoustic Materials -->
        <div class="sm:col-span-6">
            <label class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent"> Acoustic materials </label>
            <div class="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
                <div class="relative flex gap-x-3" v-for="(material, index) in materialTypes" :key="index">
                    <div class="flex h-6 items-center">
                        <input
                            :id="`material.identifier_${material.identifier}`"
                            type="checkbox"
                            name="materials"
                            :value="material.identifier"
                            v-model="formData.materials"
                            @change="syncStoreRoomData()"
                            class="h-5 w-5 border-b border-black text-accent focus:ring-accent" />
                    </div>
                    <div class="text-sm leading-6">
                        <label :for="`material.identifier_${material.identifier}`" class="font-medium text-gray-900">
                            {{ material.label }}
                        </label>
                    </div>
                </div>
            </div>
            <p class="mt-1 text-sm text-red-600" id="roomusage-error" v-if="!store.validateRoomMaterials && store.validation.Room.showErrors">
                {{ store.validation.Room.errors.roommaterials }}
            </p>
        </div>

        <!-- Close/Cancel and Save and Calculate Buttons -->
        <div class="mt-10 flex flex-col items-center gap-4 text-center sm:col-span-6 sm:flex-row sm:justify-center" v-if="store.stepReport">
            <button type="button" class="lux-btn lux-btn-small lux-btn-black" title="Save room edits and recalculate" @click="handleRoomDoneClick()">Save and Calculate</button>
            <button type="button" class="lux-btn lux-btn-small" title="Cancel and close room edit form" @click="handleRoomCancelClick()">Cancel</button>
        </div>
        <!-- Close/Cancel Button -->
        <!-- <button type="button" id="roomdataform_close" class="spin-effect lux-focus-ring absolute -right-0 -top-3 left-auto z-100 h-[25px] w-[25px] md:h-[35px] md:w-[35px]" @click="handleRoomCancelClick()" title="Cancel and close room edit form">
            <PhX aria-hidden="true" class="lux-pos-absmiddle h-full w-full text-blacka75 hover:text-black" />
        </button> -->
    </div>
</template>
